import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@up2d8/contracts';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardPad,
  Empty,
  IconBtn,
  PageHead,
  Table,
  useAdminToast,
  SwitchRow,
} from '../admin-kit';
import { ConfirmModal } from '../organisatie/confirm-modal';
import { useSession } from '../../shared/session';
import { ListError } from '../../shared/list-error';

/** De naam van een rol in de taal van de gebruiker, met het Nederlands als terugval. */
export function roleName(role: Role, language: string): string {
  return (role.name as Record<string, string | undefined>)[language] ?? role.name.nl ?? '—';
}

/**
 * Rollen: wie in dit bedrijf wat mag (O6).
 *
 * Twee dingen zijn bewust zichtbaar in plaats van verstopt. De rol Beheerder toont zijn
 * rechten wel maar laat ze niet wijzigen — anders kan een beheerder zichzelf
 * buitensluiten. En de standaardrol staat gemarkeerd, want dat is wat iemand krijgt die
 * nog niets heeft.
 *
 * Bewerken en toevoegen gebeurt op een eigen pagina (O7, `role-editor-page.tsx`): 77 rechten
 * passen niet in een venster.
 */
export function RolesPage() {
  const { t, i18n } = useTranslation();
  const { api, has, refreshPermissions } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const adminToast = useAdminToast();
  const queryClient = useQueryClient();
  const [verwijderen, setVerwijderen] = useState<Role | null>(null);

  const canManage = has('access.role.manage');
  const language = i18n.language.slice(0, 2);
  const nameOf = (role: Role): string => roleName(role, language);

  const roles = useQuery({
    queryKey: ['access', 'roles'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/roles');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { roles: Role[] }).roles;
    },
  });

  const afterChange = async () => {
    await queryClient.invalidateQueries({ queryKey: ['access'] });
    // Je kunt je eigen rol wijzigen: dan klopt de navigatie meteen.
    await refreshPermissions();
  };

  const makeDefault = useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await api.POST('/api/v1/roles/{roleId}/default', {
        params: { path: { roleId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      adminToast.show(t('roles.defaultChanged'));
      await afterChange();
    },
    onError: () => toast.show({ title: t('roles.saveFailed'), tone: 'danger' }),
  });

  const remove = useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await api.DELETE('/api/v1/roles/{roleId}', {
        params: { path: { roleId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setVerwijderen(null);
      adminToast.show(t('roles.deleted'));
      await afterChange();
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: (error as { detail?: string }).detail ?? t('roles.deleteFailed'),
      }),
  });

  const open = (roleId: string) =>
    void navigate({ to: '/admin/roles/$roleId', params: { roleId } });

  return (
    <AdminPage>
      <PageHead
        title={t('roles.title')}
        sub={t('beheer.organisatie.roles.sub')}
        actions={
          canManage ? (
            <Btn
              icon={<AdminIcon name="plus" />}
              onClick={() => void navigate({ to: '/admin/roles/new' })}
            >
              {t('roles.new')}
            </Btn>
          ) : null
        }
      />

      {has('identity.account.manage') ? <SecurityCard /> : null}

      <Card>
        {roles.isPending ? (
          <CardPad>
            <LoadingState rows={4} label={t('common.loading')} />
          </CardPad>
        ) : roles.isError ? (
          <CardPad>
            <ListError onRetry={() => void roles.refetch()} error={roles.error} />
          </CardPad>
        ) : (roles.data?.length ?? 0) === 0 ? (
          <Empty
            icon={<AdminIcon name="key" size={24} />}
            title={t('roles.empty')}
            text={t('roles.emptyHint')}
          />
        ) : (
          <Table label={t('roles.title')}>
            <thead>
              <tr>
                <th>{t('roles.name')}</th>
                <th>{t('roles.rights')}</th>
                <th className="num">{t('roles.members')}</th>
                <th className="act" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {roles.data?.map((role) => {
                // De rol Beheerder bekijk je; zijn rechten liggen vast (en wie niet mag beheren,
                // bekijkt elke rol).
                const alleenBekijken = !canManage || role.key === 'admin';
                return (
                  <tr key={role.id}>
                    <td>
                      <div className="a-name">{nameOf(role)}</div>
                      {role.isDefault || role.isSystem ? (
                        <div className="a-meta">
                          {role.isDefault ? <Badge tone="info">{t('roles.default')}</Badge> : null}{' '}
                          {role.isSystem ? <Badge tone="navy">{t('roles.system')}</Badge> : null}
                        </div>
                      ) : null}
                    </td>
                    <td>{t('roles.rightsCount', { count: role.policyKeys.length })}</td>
                    <td className="num">{role.memberCount}</td>
                    {alleenBekijken ? (
                      <td className="act">
                        <Btn variant="ghost" size="sm" onClick={() => open(role.id)}>
                          {t('roles.view')}
                        </Btn>
                      </td>
                    ) : (
                      <ActionsCell>
                        <IconBtn
                          label={t('common.edit')}
                          icon={<AdminIcon name="pencil" />}
                          testId="role-edit"
                          onClick={() => open(role.id)}
                        />
                        {!role.isDefault ? (
                          <IconBtn
                            label={t('roles.makeDefault')}
                            icon={<AdminIcon name="check" />}
                            testId="role-make-default"
                            disabled={makeDefault.isPending}
                            onClick={() => makeDefault.mutate(role.id)}
                          />
                        ) : null}
                        {/*
                          Verwijderen alleen bij een eigen rol (O6): een systeemrol pas je aan
                          maar verwijder je niet. De standaardrol eerst ontzetten (de server
                          weigert het ook).
                        */}
                        {!role.isSystem && !role.isDefault ? (
                          <IconBtn
                            label={t('common.delete')}
                            icon={<AdminIcon name="trash" />}
                            testId="role-remove"
                            onClick={() => setVerwijderen(role)}
                          />
                        ) : null}
                      </ActionsCell>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {/*
        Een rol verwijderen noemt het aantal mensen dat hem heeft (punt 13).

        Dat getal is het verschil tussen "een rol die niemand gebruikt opruimen" en "veertien
        mensen vanavond buitensluiten uit het beheerportaal".
      */}
      <ConfirmModal
        open={verwijderen !== null}
        onClose={() => setVerwijderen(null)}
        title={t('roles.delete')}
        description={t('roles.deleteConfirm', {
          name: verwijderen ? nameOf(verwijderen) : '',
          count: verwijderen?.memberCount ?? 0,
        })}
        confirmLabel={t('common.delete')}
        busy={remove.isPending}
        testId="confirm-role-remove"
        onConfirm={() => verwijderen && remove.mutate(verwijderen.id)}
      />

      {adminToast.element}
    </AdminPage>
  );
}

/**
 * Beveiliging van het bedrijf (FBK-12): tweestapsverificatie verplicht voor beheerders. Bij de
 * rollen, omdat "beheerder" hier betekent: wie een beheerrecht heeft.
 */
function SecurityCard() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['security-settings'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/security/settings', {});
      return (data as { mfaRequiredForAdmins: boolean } | undefined) ?? null;
    },
  });
  const save = useMutation({
    mutationFn: async (required: boolean) => {
      const { error } = await api.PUT('/api/v1/security/settings', {
        body: { mfaRequiredForAdmins: required } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['security-settings'] }),
  });
  if (!settings.data) return null;
  return (
    <Card testId="security-settings">
      <CardPad>
        <SwitchRow
          title={t('mfa.requiredLabel')}
          text={t('mfa.requiredHint')}
          checked={settings.data.mfaRequiredForAdmins}
          disabled={save.isPending}
          onChange={(on) => save.mutate(on)}
          testId="mfa-required"
        />
      </CardPad>
    </Card>
  );
}
